import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";

const API = `${config.API}/manager/productionplans`;

const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const emptyForm = {
    product_id: "",
    po_number: "",
    month: "",
    qty_pending: 0,
    qty_produced: 0,
    qty_delivered: 0,
};

export default function ProductionPlan() {
    const [plans, setPlans] = useState([]);
    const [products, setProducts] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState([]);

    const [form, setForm] = useState(emptyForm);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 5,
        total: 0,
        from: 0,
        to: 0,
    });

    const token = getAuthToken();

    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    };

    // =========================================================
    // PRODUCT OPTIONS
    // =========================================================

    const productOptions = useMemo(() => {
        return products.map((product) => ({
            value: product.id,
            label: product.name || `Product #${product.id}`,
        }));
    }, [products]);

    // =========================================================
    // PURCHASE ORDER OPTIONS
    // =========================================================

    const purchaseOrderOptions = useMemo(() => {
        return purchaseOrders.map((po) => ({
            value: po.id,
            label: po.po_number || `PO #${po.id}`,
        }));
    }, [purchaseOrders]);

    // =========================================================
    // REACT SELECT STYLES
    // NO EXTERNAL CSS
    // =========================================================

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "44px",
            borderRadius: "7px",
            borderColor: state.isFocused
                ? "#86b7fe"
                : "#ced4da",
            boxShadow: state.isFocused
                ? "0 0 0 0.2rem rgba(13,110,253,.12)"
                : "none",
            backgroundColor: "#fff",
            cursor: "text",
            "&:hover": {
                borderColor: "#86b7fe",
            },
        }),

        valueContainer: (base) => ({
            ...base,
            padding: "4px 10px",
            minWidth: 0,
        }),

        input: (base) => ({
            ...base,
            color: "#212529",
            margin: 0,
            padding: 0,
        }),

        placeholder: (base) => ({
            ...base,
            color: "#6c757d",
        }),

        singleValue: (base) => ({
            ...base,
            maxWidth: "calc(100% - 10px)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }),

        menuPortal: (base) => ({
            ...base,
            zIndex: 999999,
        }),

        menu: (base) => ({
            ...base,
            zIndex: 999999,
            borderRadius: "7px",
            overflow: "hidden",
            boxShadow:
                "0 8px 25px rgba(0,0,0,.18)",
        }),

        menuList: (base) => ({
            ...base,
            maxHeight: "300px",
            padding: "5px",
        }),

        option: (base, state) => ({
            ...base,
            padding: "10px 12px",
            cursor: "pointer",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: "1.45",
            borderRadius: "5px",
            backgroundColor: state.isSelected
                ? "#0d6efd"
                : state.isFocused
                ? "#f1f5ff"
                : "#fff",
            color: state.isSelected
                ? "#fff"
                : "#212529",
        }),

        indicatorsContainer: (base) => ({
            ...base,
            flexShrink: 0,
        }),

        dropdownIndicator: (base) => ({
            ...base,
            cursor: "pointer",
        }),

        clearIndicator: (base) => ({
            ...base,
            cursor: "pointer",
        }),
    };

    // =========================================================
    // GET DATA
    // =========================================================

    const getPlans = async (currentPage = 1) => {
        if (!token) {
            toast.error("Please login first.");
            return;
        }

        try {
            setLoading(true);

            const response = await axios.get(API, {
                headers,
                params: {
                    page: currentPage,
                },
            });

            if (
                response.status === 200 &&
                response.data?.success
            ) {
                const data = response.data;

                setProducts(
                    Array.isArray(data.product)
                        ? data.product
                        : []
                );

                setPurchaseOrders(
                    Array.isArray(data.po)
                        ? data.po
                        : []
                );

                const productionPlans =
                    data.productionplans || {};

                setPlans(
                    Array.isArray(
                        productionPlans.data
                    )
                        ? productionPlans.data
                        : []
                );

                setPagination({
                    current_page:
                        productionPlans.current_page ||
                        1,

                    last_page:
                        productionPlans.last_page ||
                        1,

                    per_page:
                        productionPlans.per_page ||
                        5,

                    total:
                        productionPlans.total || 0,

                    from:
                        productionPlans.from || 0,

                    to:
                        productionPlans.to || 0,
                });
            } else {
                setPlans([]);

                toast.error(
                    response.data?.message ||
                        "Failed to load production plans."
                );
            }
        } catch (error) {
            console.error(
                "Production Plans Error:",
                error
            );

            toast.error(
                error.response?.data?.message ||
                    "Failed to load production plans."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getPlans(page);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // =========================================================
    // INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // =========================================================
    // ADD
    // =========================================================

    const openAdd = () => {
        setEditingId(null);
        setForm({ ...emptyForm });
        setShowModal(true);
    };

    // =========================================================
    // EDIT
    // =========================================================

    const openEdit = (item) => {
        setEditingId(item.id);

        setForm({
            product_id: item.product_id ?? "",
            po_number: item.po_number ?? "",
            month: item.month ?? "",
            qty_pending: item.qty_pending ?? 0,
            qty_produced: item.qty_produced ?? 0,
            qty_delivered:
                item.qty_delivered ?? 0,
        });

        setShowModal(true);
    };

    // =========================================================
    // CLOSE
    // =========================================================

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm({ ...emptyForm });
    };

    // =========================================================
    // SAVE / UPDATE
    // =========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.product_id) {
            toast.error("Please select a product.");
            return;
        }

        if (!form.po_number) {
            toast.error(
                "Please select a purchase order."
            );
            return;
        }

        if (!form.month) {
            toast.error("Please select a month.");
            return;
        }

        try {
            setSaving(true);

            const payload = {
                product_id: Number(
                    form.product_id
                ),
                po_number: Number(
                    form.po_number
                ),
                month: form.month,
                qty_pending: Number(
                    form.qty_pending || 0
                ),
                qty_produced: Number(
                    form.qty_produced || 0
                ),
                qty_delivered: Number(
                    form.qty_delivered || 0
                ),
            };

            let response;

            if (editingId) {
                response = await axios.put(
                    `${API}/${editingId}`,
                    payload,
                    { headers }
                );
            } else {
                response = await axios.post(
                    API,
                    payload,
                    { headers }
                );
            }

            if (
                (response.status === 200 ||
                    response.status === 201) &&
                response.data?.success !== false
            ) {
                toast.success(
                    editingId
                        ? "Production plan updated successfully."
                        : "Production plan created successfully."
                );

                closeModal();
                await getPlans(page);
            } else {
                toast.error(
                    response.data?.message ||
                        "Operation failed."
                );
            }
        } catch (error) {
            console.error(
                "Save Production Plan Error:",
                error
            );

            toast.error(
                error.response?.data?.message ||
                    "Failed to save production plan."
            );
        } finally {
            setSaving(false);
        }
    };

    // =========================================================
    // DELETE
    // =========================================================

    const handleDelete = async (id) => {
        if (
            !window.confirm(
                "Are you sure you want to delete this production plan?"
            )
        ) {
            return;
        }

        try {
            setLoading(true);

            const response = await axios.delete(
                `${API}/${id}`,
                { headers }
            );

            if (
                response.status === 200 &&
                response.data?.success !== false
            ) {
                toast.success(
                    "Production plan deleted successfully."
                );

                if (
                    plans.length === 1 &&
                    page > 1
                ) {
                    setPage(
                        (prev) => prev - 1
                    );
                } else {
                    await getPlans(page);
                }
            } else {
                toast.error(
                    response.data?.message ||
                        "Failed to delete production plan."
                );
            }
        } catch (error) {
            console.error(
                "Delete Production Plan Error:",
                error
            );

            toast.error(
                error.response?.data?.message ||
                    "Failed to delete production plan."
            );
        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // TABLE SEARCH
    // =========================================================

    const filteredPlans = useMemo(() => {
        const value = search
            .toLowerCase()
            .trim();

        if (!value) {
            return plans;
        }

        return plans.filter((item) => {
            const productName =
                item.product?.name || "";

            const poName =
                item.purchase_order
                    ?.po_number || "";

            const userName =
                item.user?.name || "";

            return (
                String(item.id)
                    .toLowerCase()
                    .includes(value) ||
                String(item.product_id)
                    .toLowerCase()
                    .includes(value) ||
                String(item.po_number)
                    .toLowerCase()
                    .includes(value) ||
                String(item.month)
                    .toLowerCase()
                    .includes(value) ||
                productName
                    .toLowerCase()
                    .includes(value) ||
                poName
                    .toLowerCase()
                    .includes(value) ||
                userName
                    .toLowerCase()
                    .includes(value)
            );
        });
    }, [plans, search]);

    // =========================================================
    // PAGINATION
    // =========================================================

    const paginationPages = useMemo(() => {
        const current =
            pagination.current_page;

        const last =
            pagination.last_page;

        if (last <= 7) {
            return Array.from(
                { length: last },
                (_, index) => index + 1
            );
        }

        const pages = [1];

        if (current > 3) {
            pages.push("...");
        }

        const start = Math.max(
            2,
            current - 1
        );

        const end = Math.min(
            last - 1,
            current + 1
        );

        for (
            let i = start;
            i <= end;
            i++
        ) {
            pages.push(i);
        }

        if (current < last - 2) {
            pages.push("...");
        }

        pages.push(last);

        return pages;
    }, [
        pagination.current_page,
        pagination.last_page,
    ]);

    const goToPage = (newPage) => {
        if (
            newPage < 1 ||
            newPage > pagination.last_page ||
            newPage ===
                pagination.current_page
        ) {
            return;
        }

        setPage(newPage);
        setSearch("");
    };

    // =========================================================
    // HELPERS
    // =========================================================

    const formatNumber = (value) => {
        return Number(value || 0).toLocaleString(
            "en-IN"
        );
    };

    const getProductName = (item) => {
        return (
            item.product?.name ||
            products.find(
                (product) =>
                    Number(product.id) ===
                    Number(item.product_id)
            )?.name ||
            `Product #${item.product_id}`
        );
    };

    const getPOName = (item) => {
        return (
            item.purchase_order?.po_number ||
            purchaseOrders.find(
                (po) =>
                    Number(po.id) ===
                    Number(item.po_number)
            )?.po_number ||
            `PO #${item.po_number}`
        );
    };

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div className="container-fluid py-3">

            {/* HEADER */}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">

                <div>
                    <h4 className="fw-bold mb-1">
                        Production Plans
                    </h4>

                    <div className="text-muted small">
                        Manage monthly production planning
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={openAdd}
                >
                    <i className="bi bi-plus-lg me-2"></i>
                    Add Production Plan
                </button>

            </div>

            {/* SUMMARY */}

            <div className="row g-3 mb-4">

                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small mb-1">
                                Total Plans
                            </div>

                            <div className="fs-4 fw-bold">
                                {pagination.total}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small mb-1">
                                Current Page
                            </div>

                            <div className="fs-4 fw-bold">
                                {
                                    pagination.current_page
                                }

                                <span className="text-muted fs-6">
                                    {" "}
                                    /{" "}
                                    {
                                        pagination.last_page
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small mb-1">
                                Showing Records
                            </div>

                            <div className="fs-4 fw-bold">
                                {pagination.from ||
                                    0}

                                <span className="text-muted fs-6">
                                    {" "}
                                    -{" "}
                                    {pagination.to ||
                                        0}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* SEARCH */}

            <div className="card border-0 shadow-sm mb-3">

                <div className="card-body">

                    <div className="row g-2">

                        <div className="col-md-10">

                            <div className="input-group">

                                <span className="input-group-text bg-white">
                                    <i className="bi bi-search"></i>
                                </span>

                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search current page..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(
                                            e.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>

                        <div className="col-md-2">

                            <button
                                type="button"
                                className="btn btn-outline-secondary w-100"
                                onClick={() =>
                                    setSearch("")
                                }
                            >
                                Clear
                            </button>

                        </div>

                    </div>

                </div>

            </div>

            {/* TABLE */}

            <div className="card border-0 shadow-sm">

                <div className="card-header bg-white border-0 py-3">

                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">

                        <div>
                            <h6 className="mb-0 fw-bold">
                                Production Plan List
                            </h6>

                            <small className="text-muted">
                                {
                                    filteredPlans.length
                                }{" "}
                                records on this page
                            </small>
                        </div>

                        <div className="text-muted small">
                            Page{" "}
                            <strong>
                                {
                                    pagination.current_page
                                }
                            </strong>{" "}
                            of{" "}
                            <strong>
                                {
                                    pagination.last_page
                                }
                            </strong>
                        </div>

                    </div>

                </div>

                <div className="card-body p-0">

                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0">

                            <thead className="table-light">

                                <tr>

                                    <th className="px-3">
                                        #
                                    </th>

                                    <th>
                                        Product
                                    </th>

                                    <th>
                                        Purchase Order
                                    </th>

                                    <th>
                                        Month
                                    </th>

                                    <th className="text-end">
                                        Produced
                                    </th>

                                    <th className="text-end">
                                        Pending
                                    </th>

                                    <th className="text-end">
                                        Delivered
                                    </th>

                                    <th>
                                        Created By
                                    </th>

                                    <th className="text-center">
                                        Action
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {loading && (
                                    <tr>
                                        <td
                                            colSpan="9"
                                            className="text-center py-5"
                                        >
                                            <div
                                                className="spinner-border text-primary"
                                                role="status"
                                            ></div>

                                            <div className="mt-2 text-muted">
                                                Loading...
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {!loading &&
                                    filteredPlans.length ===
                                        0 && (
                                        <tr>
                                            <td
                                                colSpan="9"
                                                className="text-center py-5"
                                            >
                                                <i className="bi bi-inbox fs-2 d-block mb-2 text-muted"></i>

                                                <span className="text-muted">
                                                    No production plans found.
                                                </span>
                                            </td>
                                        </tr>
                                    )}

                                {!loading &&
                                    filteredPlans.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    item.id
                                                }
                                            >

                                                <td className="px-3 fw-semibold">
                                                    {(pagination.from ||
                                                        1) +
                                                        index}
                                                </td>

                                                {/* PRODUCT */}

                                                <td
                                                    style={{
                                                        minWidth:
                                                            "280px",
                                                        maxWidth:
                                                            "400px",
                                                        whiteSpace:
                                                            "normal",
                                                        overflowWrap:
                                                            "anywhere",
                                                        wordBreak:
                                                            "break-word",
                                                    }}
                                                >
                                                    <div
                                                        className="fw-semibold"
                                                        style={{
                                                            whiteSpace:
                                                                "normal",
                                                            overflowWrap:
                                                                "anywhere",
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        {getProductName(
                                                            item
                                                        )}
                                                    </div>

                                                    <small className="text-muted">
                                                        Product ID:{" "}
                                                        {
                                                            item.product_id
                                                        }
                                                    </small>
                                                </td>

                                                {/* PURCHASE ORDER */}

                                                <td
                                                    style={{
                                                        minWidth:
                                                            "260px",
                                                        maxWidth:
                                                            "400px",
                                                        whiteSpace:
                                                            "normal",
                                                        overflowWrap:
                                                            "anywhere",
                                                        wordBreak:
                                                            "break-word",
                                                    }}
                                                >
                                                    <div
                                                        className="fw-semibold"
                                                        style={{
                                                            whiteSpace:
                                                                "normal",
                                                            overflowWrap:
                                                                "anywhere",
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        {getPOName(
                                                            item
                                                        )}
                                                    </div>

                                                    <small className="text-muted">
                                                        PO ID:{" "}
                                                        {
                                                            item.po_number
                                                        }
                                                    </small>
                                                </td>

                                                {/* MONTH */}

                                                <td>
                                                    <span className="badge bg-primary-subtle text-primary">
                                                        {item.month ||
                                                            "-"}
                                                    </span>
                                                </td>

                                                {/* PRODUCED */}

                                                <td className="text-end">
                                                    <span className="badge bg-success-subtle text-success fs-6">
                                                        {formatNumber(
                                                            item.qty_produced
                                                        )}
                                                    </span>
                                                </td>

                                                {/* PENDING */}

                                                <td className="text-end">
                                                    <span className="badge bg-warning-subtle text-warning-emphasis fs-6">
                                                        {formatNumber(
                                                            item.qty_pending
                                                        )}
                                                    </span>
                                                </td>

                                                {/* DELIVERED */}

                                                <td className="text-end">
                                                    <span className="badge bg-info-subtle text-info-emphasis fs-6">
                                                        {formatNumber(
                                                            item.qty_delivered
                                                        )}
                                                    </span>
                                                </td>

                                                {/* CREATED BY */}

                                                <td>
                                                    <div className="fw-semibold">
                                                        {item.user
                                                            ?.name ||
                                                            "-"}
                                                    </div>

                                                    <small className="text-muted">
                                                        User ID:{" "}
                                                        {
                                                            item.user_id
                                                        }
                                                    </small>
                                                </td>

                                                {/* ACTION */}

                                                <td className="text-center">

                                                    <div className="d-flex justify-content-center gap-2">

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="Edit"
                                                            onClick={() =>
                                                                openEdit(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-pencil-square"></i>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            title="Delete"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    item.id
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>
                                        )
                                    )}

                            </tbody>

                        </table>

                    </div>

                </div>

                {/* PAGINATION */}

                {pagination.last_page > 1 && (
                    <div className="card-footer bg-white border-0 py-3">

                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">

                            <div className="text-muted small">
                                Showing{" "}
                                <strong>
                                    {pagination.from || 0}
                                </strong>{" "}
                                to{" "}
                                <strong>
                                    {pagination.to || 0}
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {pagination.total || 0}
                                </strong>{" "}
                                records
                            </div>

                            <nav>
                                <ul className="pagination pagination-sm mb-0">

                                    <li
                                        className={`page-item ${
                                            pagination.current_page ===
                                            1
                                                ? "disabled"
                                                : ""
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="page-link"
                                            onClick={() =>
                                                goToPage(
                                                    pagination.current_page -
                                                        1
                                                )
                                            }
                                            disabled={
                                                pagination.current_page ===
                                                1
                                            }
                                        >
                                            <i className="bi bi-chevron-left"></i>
                                        </button>
                                    </li>

                                    {paginationPages.map(
                                        (
                                            pageNumber,
                                            index
                                        ) => {
                                            if (
                                                pageNumber ===
                                                "..."
                                            ) {
                                                return (
                                                    <li
                                                        key={`dots-${index}`}
                                                        className="page-item disabled"
                                                    >
                                                        <span className="page-link">
                                                            ...
                                                        </span>
                                                    </li>
                                                );
                                            }

                                            return (
                                                <li
                                                    key={
                                                        pageNumber
                                                    }
                                                    className={`page-item ${
                                                        pagination.current_page ===
                                                        pageNumber
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        className="page-link"
                                                        onClick={() =>
                                                            goToPage(
                                                                pageNumber
                                                            )
                                                        }
                                                    >
                                                        {
                                                            pageNumber
                                                        }
                                                    </button>
                                                </li>
                                            );
                                        }
                                    )}

                                    <li
                                        className={`page-item ${
                                            pagination.current_page ===
                                            pagination.last_page
                                                ? "disabled"
                                                : ""
                                        }`}
                                    >
                                        <button
                                            type="button"
                                            className="page-link"
                                            onClick={() =>
                                                goToPage(
                                                    pagination.current_page +
                                                        1
                                                )
                                            }
                                            disabled={
                                                pagination.current_page ===
                                                pagination.last_page
                                            }
                                        >
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                    </li>

                                </ul>
                            </nav>

                        </div>

                    </div>
                )}

            </div>

            {/* =================================================
                MODAL
            ================================================= */}

            {showModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{
                        backgroundColor:
                            "rgba(0,0,0,.55)",
                    }}
                >

                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">

                        <div className="modal-content border-0 shadow-lg">

                            {/* MODAL HEADER */}

                            <div className="modal-header">

                                <div>
                                    <h5 className="modal-title fw-bold mb-1">
                                        {editingId
                                            ? "Edit Production Plan"
                                            : "Add Production Plan"}
                                    </h5>

                                    <small className="text-muted">
                                        {editingId
                                            ? "Update production plan"
                                            : "Create a new production plan"}
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={closeModal}
                                ></button>

                            </div>

                            <form onSubmit={handleSubmit}>

                                <div className="modal-body">

                                    <div className="row g-3">

                                        {/* =================================================
                                            PRODUCT - REACT SELECT
                                        ================================================= */}

                                        <div className="col-md-12">

                                            <label className="form-label fw-semibold">
                                                Product
                                                <span className="text-danger ms-1">
                                                    *
                                                </span>
                                            </label>

                                            <Select
                                                options={
                                                    productOptions
                                                }
                                                value={
                                                    productOptions.find(
                                                        (option) =>
                                                            String(
                                                                option.value
                                                            ) ===
                                                            String(
                                                                form.product_id
                                                            )
                                                    ) || null
                                                }
                                                onChange={(
                                                    selected
                                                ) => {
                                                    setForm(
                                                        (
                                                            prev
                                                        ) => ({
                                                            ...prev,
                                                            product_id:
                                                                selected?.value ||
                                                                "",
                                                        })
                                                    );
                                                }}
                                                placeholder="Search product..."
                                                isSearchable={true}
                                                isClearable={true}
                                                isDisabled={
                                                    saving
                                                }
                                                styles={
                                                    selectStyles
                                                }
                                                menuPortalTarget={
                                                    document.body
                                                }
                                                menuPosition="fixed"
                                                menuPlacement="auto"
                                                maxMenuHeight={
                                                    300
                                                }
                                                noOptionsMessage={({
                                                    inputValue,
                                                }) =>
                                                    inputValue
                                                        ? `No product found for "${inputValue}"`
                                                        : "No products available"
                                                }
                                            />

                                            <small className="text-muted">
                                                Type to search product
                                            </small>

                                        </div>

                                        {/* =================================================
                                            PURCHASE ORDER - REACT SELECT
                                        ================================================= */}

                                        <div className="col-md-12">

                                            <label className="form-label fw-semibold">
                                                Purchase Order
                                                <span className="text-danger ms-1">
                                                    *
                                                </span>
                                            </label>

                                            <Select
                                                options={
                                                    purchaseOrderOptions
                                                }
                                                value={
                                                    purchaseOrderOptions.find(
                                                        (option) =>
                                                            String(
                                                                option.value
                                                            ) ===
                                                            String(
                                                                form.po_number
                                                            )
                                                    ) || null
                                                }
                                                onChange={(
                                                    selected
                                                ) => {
                                                    setForm(
                                                        (
                                                            prev
                                                        ) => ({
                                                            ...prev,
                                                            po_number:
                                                                selected?.value ||
                                                                "",
                                                        })
                                                    );
                                                }}
                                                placeholder="Search purchase order..."
                                                isSearchable={true}
                                                isClearable={true}
                                                isDisabled={
                                                    saving
                                                }
                                                styles={
                                                    selectStyles
                                                }
                                                menuPortalTarget={
                                                    document.body
                                                }
                                                menuPosition="fixed"
                                                menuPlacement="auto"
                                                maxMenuHeight={
                                                    300
                                                }
                                                noOptionsMessage={({
                                                    inputValue,
                                                }) =>
                                                    inputValue
                                                        ? `No purchase order found for "${inputValue}"`
                                                        : "No purchase orders available"
                                                }
                                            />

                                            <small className="text-muted">
                                                Type to search purchase order
                                            </small>

                                        </div>

                                        {/* MONTH */}

                                        <div className="col-md-4">

                                            <label className="form-label fw-semibold">
                                                Month
                                                <span className="text-danger ms-1">
                                                    *
                                                </span>
                                            </label>

                                            <select
                                                className="form-select"
                                                name="month"
                                                value={
                                                    form.month
                                                }
                                                onChange={
                                                    handleChange
                                                }
                                                required
                                            >
                                                <option value="">
                                                    Select Month
                                                </option>

                                                {months.map(
                                                    (month) => (
                                                        <option
                                                            key={
                                                                month
                                                            }
                                                            value={
                                                                month
                                                            }
                                                        >
                                                            {
                                                                month
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>

                                        </div>

                                        {/* PRODUCED */}

                                        <div className="col-md-4">

                                            <label className="form-label fw-semibold">
                                                Qty Produced
                                            </label>

                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control"
                                                name="qty_produced"
                                                value={
                                                    form.qty_produced
                                                }
                                                onChange={
                                                    handleChange
                                                }
                                            />

                                        </div>

                                        {/* PENDING */}

                                        <div className="col-md-4">

                                            <label className="form-label fw-semibold">
                                                Qty Pending
                                            </label>

                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control"
                                                name="qty_pending"
                                                value={
                                                    form.qty_pending
                                                }
                                                onChange={
                                                    handleChange
                                                }
                                            />

                                        </div>

                                        {/* DELIVERED */}

                                        <div className="col-md-4">

                                            <label className="form-label fw-semibold">
                                                Qty Delivered
                                            </label>

                                            <input
                                                type="number"
                                                min="0"
                                                className="form-control"
                                                name="qty_delivered"
                                                value={
                                                    form.qty_delivered
                                                }
                                                onChange={
                                                    handleChange
                                                }
                                            />

                                        </div>

                                    </div>

                                </div>

                                {/* FOOTER */}

                                <div className="modal-footer">

                                    <button
                                        type="button"
                                        className="btn btn-light border"
                                        onClick={
                                            closeModal
                                        }
                                        disabled={
                                            saving
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={
                                            saving
                                        }
                                    >

                                        {saving ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                ></span>

                                                Saving...
                                            </>
                                        ) : editingId ? (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Update Plan
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-plus-lg me-2"></i>
                                                Save Plan
                                            </>
                                        )}

                                    </button>

                                </div>

                            </form>

                        </div>

                    </div>

                </div>
            )}

        </div>
    );
}